import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export const Contact: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    industry: '',
    message: '',
    plan: 'Essential'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    // Header scroll effect
    const handleScroll = () => {
      const header = document.querySelector('.header');
      if (window.scrollY > 50) {
        header?.classList.add('scrolled');
      } else {
        header?.classList.remove('scrolled');
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call - replace with your actual endpoint
    try {
      // await fetch('/api/contact', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(formData)
      // });
      
      // Simulate delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        industry: '',
        message: '',
        plan: 'Essential'
      });
      
      // Reset success message after 5 seconds
      setTimeout(() => setSubmitStatus('idle'), 5000);
    } catch (error) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleMobileMenu = () => {
    const navMenu = document.querySelector('.nav-menu') as HTMLElement;
    if (navMenu.style.display === 'flex') {
      navMenu.style.display = 'none';
    } else {
      navMenu.style.display = 'flex';
      navMenu.style.flexDirection = 'column';
      navMenu.style.position = 'absolute';
      navMenu.style.top = '100%';
      navMenu.style.left = '0';
      navMenu.style.right = '0';
      navMenu.style.background = 'white';
      navMenu.style.padding = '2rem';
      navMenu.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.1)';
      navMenu.style.zIndex = '1000';
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700;800&family=Poppins:wght@300;400;500;600;700&display=swap');

        :root {
          --font-heading: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          --font-body: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          --primary: #0A0E27;
          --accent: #00E5A0;
          --accent-secondary: #00B8D4;
          --text-dark: #0A0E27;
          --text-light: #5A607F;
          --text-muted: #9EA4B8;
          --bg-light: #F8FAFB;
          --bg-white: #fcfadd;
          --border: #E5E9F2;
          --error: #FF3D5A;
          --success: #00E5A0;
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        body {
          font-family: var(--font-body);
          line-height: 1.6;
          color: var(--text-dark);
          background: var(--bg-white);
        }

        /* Contact Hero */
        .contact-hero {
          padding: 160px 2rem 80px;
          background: linear-gradient(135deg, 
            rgba(0, 229, 160, 0.05) 0%, 
            rgba(0, 184, 212, 0.05) 100%),
            var(--bg-white);
          text-align: center;
          position: relative;
          overflow: hidden;
        }

        .contact-hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image: 
            linear-gradient(var(--border) 1px, transparent 1px),
            linear-gradient(90deg, var(--border) 1px, transparent 1px);
          background-size: 50px 50px;
          opacity: 0.3;
          pointer-events: none;
        }

        .contact-hero-content {
          max-width: 900px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }

        .contact-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1.25rem;
          background: rgba(0, 229, 160, 0.1);
          border: 1px solid rgba(0, 229, 160, 0.3);
          border-radius: 50px;
          font-size: 0.85rem;
          font-weight: 700;
          color: var(--primary);
          margin-bottom: 2rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .contact-hero h1 {
          font-family: var(--font-heading);
          font-size: clamp(2.5rem, 5vw, 4rem);
          font-weight: 800;
          margin-bottom: 1.5rem;
          letter-spacing: -0.02em;
          line-height: 1.1;
        }

        .contact-hero .highlight {
          background: linear-gradient(135deg, var(--accent) 0%, var(--accent-secondary) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .contact-hero p {
          font-size: clamp(1.1rem, 2vw, 1.3rem);
          color: var(--text-light);
          line-height: 1.7;
          max-width: 700px;
          margin: 0 auto;
        }

        /* Contact Methods Section */
        .contact-methods {
          padding: 80px 2rem;
          background: var(--bg-white);
        }

        .contact-methods-container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .contact-methods-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
          margin-top: 3rem;
        }

        .contact-method-card {
          background: var(--bg-light);
          padding: 2.5rem;
          border-radius: 20px;
          border: 2px solid var(--border);
          transition: all 0.4s ease;
          text-align: center;
        }

        .contact-method-card:hover {
          transform: translateY(-8px);
          border-color: var(--accent);
          box-shadow: 0 20px 48px rgba(0, 229, 160, 0.15);
        }

        .contact-method-icon {
          width: 72px;
          height: 72px;
          background: linear-gradient(135deg, var(--accent) 0%, var(--accent-secondary) 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          color: var(--primary);
          font-size: 2rem;
          box-shadow: 0 8px 24px rgba(0, 229, 160, 0.25);
        }

        .contact-method-card h3 {
          font-family: var(--font-heading);
          font-size: 1.5rem;
          font-weight: 800;
          margin-bottom: 0.75rem;
          color: var(--text-dark);
        }

        .contact-method-card p {
          color: var(--text-light);
          margin-bottom: 1rem;
          line-height: 1.6;
        }

        .contact-method-link {
          color: var(--accent);
          font-weight: 700;
          text-decoration: none;
          font-size: 1.1rem;
          transition: color 0.3s ease;
        }

        .contact-method-link:hover {
          color: var(--accent-secondary);
        }

        /* Contact Form Section */
        .contact-form-section {
          padding: 80px 2rem;
          background: var(--bg-light);
        }

        .form-container {
          max-width: 800px;
          margin: 0 auto;
        }

        .section-title {
          font-family: var(--font-heading);
          font-size: clamp(2rem, 4vw, 3rem);
          font-weight: 800;
          text-align: center;
          margin-bottom: 1rem;
          letter-spacing: -0.02em;
        }

        .section-subtitle {
          text-align: center;
          color: var(--text-light);
          font-size: 1.15rem;
          margin-bottom: 3rem;
          line-height: 1.7;
        }

        .contact-form {
          background: var(--bg-white);
          padding: 3rem;
          border-radius: 24px;
          border: 2px solid var(--border);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05);
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
          margin-bottom: 1.5rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-group.full-width {
          grid-column: 1 / -1;
        }

        .form-label {
          font-weight: 600;
          color: var(--text-dark);
          font-size: 0.95rem;
        }

        .form-label .required {
          color: var(--error);
        }

        .form-input,
        .form-select,
        .form-textarea {
          padding: 1rem 1.25rem;
          border: 2px solid var(--border);
          border-radius: 12px;
          font-size: 1rem;
          font-family: var(--font-body);
          background: var(--bg-white);
          color: var(--text-dark);
          transition: all 0.3s ease;
        }

        .form-input:focus,
        .form-select:focus,
        .form-textarea:focus {
          outline: none;
          border-color: var(--accent);
          box-shadow: 0 0 0 4px rgba(0, 229, 160, 0.1);
        }

        .form-textarea {
          min-height: 150px;
          resize: vertical;
        }

        .form-submit {
          width: 100%;
          padding: 1.25rem;
          background: linear-gradient(135deg, var(--accent) 0%, var(--accent-secondary) 100%);
          color: var(--primary);
          border: none;
          border-radius: 12px;
          font-size: 1.1rem;
          font-weight: 700;
          font-family: var(--font-body);
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 16px rgba(0, 229, 160, 0.3);
        }

        .form-submit:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 24px rgba(0, 229, 160, 0.4);
        }

        .form-submit:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .form-status {
          margin-top: 1.5rem;
          padding: 1.25rem;
          border-radius: 12px;
          text-align: center;
          font-weight: 600;
        }

        .form-status.success {
          background: rgba(0, 229, 160, 0.1);
          border: 2px solid var(--accent);
          color: var(--primary);
        }

        .form-status.error {
          background: rgba(255, 61, 90, 0.1);
          border: 2px solid var(--error);
          color: var(--error);
        }

        /* FAQ Section */
        .contact-faq {
          padding: 80px 2rem;
          background: var(--bg-white);
        }

        .faq-container {
          max-width: 800px;
          margin: 0 auto;
        }

        .faq-item {
          background: var(--bg-light);
          padding: 2rem;
          border-radius: 16px;
          margin-bottom: 1.25rem;
          border: 2px solid var(--border);
          transition: all 0.3s ease;
        }

        .faq-item:hover {
          border-color: var(--accent);
          box-shadow: 0 8px 24px rgba(0, 229, 160, 0.1);
        }

        .faq-question {
          font-family: var(--font-heading);
          font-size: 1.2rem;
          font-weight: 700;
          margin-bottom: 0.75rem;
          color: var(--text-dark);
        }

        .faq-answer {
          color: var(--text-light);
          line-height: 1.7;
          font-size: 1.05rem;
        }

        /* Office Hours */
        .office-hours {
          padding: 80px 2rem;
          background: var(--bg-light);
        }

        .office-hours-container {
          max-width: 1000px;
          margin: 0 auto;
          text-align: center;
        }

        .hours-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 2rem;
          margin-top: 3rem;
        }

        .hours-card {
          background: var(--bg-white);
          padding: 2.5rem;
          border-radius: 20px;
          border: 2px solid var(--border);
        }

        .hours-card h3 {
          font-family: var(--font-heading);
          font-size: 1.5rem;
          font-weight: 800;
          margin-bottom: 1.5rem;
          color: var(--text-dark);
        }

        .hours-list {
          text-align: left;
          color: var(--text-light);
          line-height: 2;
        }

        .hours-list div {
          display: flex;
          justify-content: space-between;
          padding: 0.5rem 0;
          border-bottom: 1px solid var(--border);
        }

        .hours-list div:last-child {
          border-bottom: none;
        }

        .day {
          font-weight: 600;
          color: var(--text-dark);
        }

        .time {
          color: var(--text-light);
        }

        /* Responsive */
        @media (max-width: 768px) {
          .contact-hero {
            padding: 120px 1.5rem 60px;
          }

          .contact-form {
            padding: 2rem 1.5rem;
          }

          .form-grid {
            grid-template-columns: 1fr;
          }

          .hours-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      {/* Header */}
      <header className="header">
        <nav className="nav-container">
          <a href="/">
            <img src="/images/8EYtClIJaotk40RaLdi5bBD444-1.png" alt="Gerbo Logo" className="logo" />
          </a>
          
          <button className="mobile-menu-toggle" aria-label="Toggle menu" onClick={toggleMobileMenu}>
            ☰
          </button>
          
          <ul className="nav-menu">
            <li><a href="/#features">Features</a></li>
            <li><a href="/#pricing">Pricing</a></li>
            <li><a href="/#industries">Industries</a></li>
            <li><a href="/#faqs">FAQ</a></li>
            <li><a href="/contact" style={{ color: 'var(--accent)' }}>Contact</a></li>
            <li><a href="/blog">Blog</a></li>
            <li>
              <button onClick={() => navigate('/login')} className="btn btn-primary">
                Try Gerbo Live
              </button>
            </li>
          </ul>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="contact-hero">
        <div className="contact-hero-content">
          <span className="contact-badge">💬 We're Here to Help</span>
          <h1>
            Get in <span className="highlight">Touch</span>
          </h1>
          <p>
            Have questions about Gerbo? Want to see a demo? Our team is ready to help you 
            transform how your business handles phone calls.
          </p>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="contact-methods">
        <div className="contact-methods-container">
          <div className="contact-methods-grid">
            <div className="contact-method-card">
              <div className="contact-method-icon">📧</div>
              <h3>Email Us</h3>
              <p>Get a response within 24 hours</p>
              <a href="mailto:support@gerboai.com" className="contact-method-link">
                support@gerboai.com
              </a>
            </div>

            <div className="contact-method-card">
              <div className="contact-method-icon">📞</div>
              <h3>Call Us</h3>
              <p>Speak to our team directly</p>
              <a href="tel:+27684887039" className="contact-method-link">
                +27 68 488 7039
              </a>
            </div>

            <div className="contact-method-card">
              <div className="contact-method-icon">💬</div>
              <h3>Live Chat</h3>
              <p>Chat with Gerbo right now</p>
              <span className="contact-method-link" style={{ cursor: 'pointer' }}>
                Start Chat →
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="contact-form-section">
        <div className="form-container">
          <h2 className="section-title">Send Us a Message</h2>
          <p className="section-subtitle">
            Fill out the form below and we'll get back to you as soon as possible.
          </p>

          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">
                  Full Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  className="form-input"
                  placeholder="John Smith"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  Email Address <span className="required">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  className="form-input"
                  placeholder="john@company.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  className="form-input"
                  placeholder="+27 12 345 6789"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Company Name</label>
                <input
                  type="text"
                  name="company"
                  className="form-input"
                  placeholder="Your Company"
                  value={formData.company}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Industry</label>
                <select
                  name="industry"
                  className="form-select"
                  value={formData.industry}
                  onChange={handleInputChange}
                >
                  <option value="">Select your industry</option>
                  <option value="estate">Estate Agents</option>
                  <option value="healthcare">Healthcare & Dentists</option>
                  <option value="restaurant">Restaurants & Bars</option>
                  <option value="tradesman">Tradesmen & Contractors</option>
                  <option value="salon">Salons & Spas</option>
                  <option value="legal">Legal Services</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Interested Plan</label>
                <select
                  name="plan"
                  className="form-select"
                  value={formData.plan}
                  onChange={handleInputChange}
                >
                  <option value="Essential">Essential ($59/mo)</option>
                  <option value="Pro">Pro ($79/mo)</option>
                  <option value="Elite">Elite ($189/mo)</option>
                  <option value="Custom">Custom Solution</option>
                </select>
              </div>

              <div className="form-group full-width">
                <label className="form-label">
                  Message <span className="required">*</span>
                </label>
                <textarea
                  name="message"
                  className="form-textarea"
                  placeholder="Tell us about your business and how we can help..."
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <button type="submit" className="form-submit" disabled={isSubmitting}>
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>

            {submitStatus === 'success' && (
              <div className="form-status success">
                ✅ Thank you! We've received your message and will respond within 24 hours.
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="form-status error">
                ❌ Something went wrong. Please try again or email us directly at support@gerboai.com
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Office Hours */}
      <section className="office-hours">
        <div className="office-hours-container">
          <h2 className="section-title">When We're Available</h2>
          <p className="section-subtitle">
            Our support team is here to help during these hours. 
            But remember—Gerbo answers your calls 24/7!
          </p>

          <div className="hours-grid">
            <div className="hours-card">
              <h3>📞 Phone Support</h3>
              <div className="hours-list">
                <div>
                  <span className="day">Monday - Friday</span>
                  <span className="time">8:00 AM - 6:00 PM</span>
                </div>
                <div>
                  <span className="day">Saturday</span>
                  <span className="time">9:00 AM - 2:00 PM</span>
                </div>
                <div>
                  <span className="day">Sunday</span>
                  <span className="time">Closed</span>
                </div>
              </div>
            </div>

            <div className="hours-card">
              <h3>📧 Email Support</h3>
              <div className="hours-list">
                <div>
                  <span className="day">Response Time</span>
                  <span className="time">Within 24 hours</span>
                </div>
                <div>
                  <span className="day">Priority Support</span>
                  <span className="time">Within 4 hours</span>
                </div>
                <div>
                  <span className="day">Weekend Inquiries</span>
                  <span className="time">Monday response</span>
                </div>
              </div>
            </div>

            <div className="hours-card">
              <h3>💬 Live Chat</h3>
              <div className="hours-list">
                <div>
                  <span className="day">Availability</span>
                  <span className="time">24/7</span>
                </div>
                <div>
                  <span className="day">AI Assistant</span>
                  <span className="time">Instant answers</span>
                </div>
                <div>
                  <span className="day">Human Agent</span>
                  <span className="time">During office hours</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="contact-faq">
        <div className="faq-container">
          <h2 className="section-title">Quick Answers</h2>
          <p className="section-subtitle">
            Common questions about getting started with Gerbo
          </p>

          <div className="faq-item">
            <h3 className="faq-question">How quickly can I get started?</h3>
            <p className="faq-answer">
              Most businesses are up and running within 24 hours. We handle the technical setup, 
              and all you need to do is forward your calls to Gerbo's number. Our onboarding team 
              will guide you through every step.
            </p>
          </div>

          <div className="faq-item">
            <h3 className="faq-question">Do I need any special equipment?</h3>
            <p className="faq-answer">
              Not at all! Gerbo works with your existing phone system. No hardware installation, 
              no software downloads. Just simple call forwarding that takes minutes to set up.
            </p>
          </div>

          <div className="faq-item">
            <h3 className="faq-question">Can I try Gerbo before committing?</h3>
            <p className="faq-answer">
              Yes! We offer a free trial period so you can experience how Gerbo handles your calls. 
              You'll see the quality of conversations and call summaries before making any commitment.
            </p>
          </div>

          <div className="faq-item">
            <h3 className="faq-question">What if I need help after setup?</h3>
            <p className="faq-answer">
              Our support team is always here to help. All plans include email and chat support. 
              Pro and Elite plans get priority support with faster response times. You'll never be 
              left on your own.
            </p>
          </div>

          <div className="faq-item">
            <h3 className="faq-question">Is my data secure and compliant?</h3>
            <p className="faq-answer">
              Absolutely. Gerbo is GDPR compliant and uses enterprise-grade security. All call data 
              is encrypted, and we're based in the SADC region. Your business data stays private 
              and secure.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="article-cta" style={{ padding: '100px 2rem', background: 'var(--primary)', textAlign: 'center', color: 'var(--bg-white)' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 800, marginBottom: '1.5rem' }}>
            Ready to Stop Missing Calls?
          </h2>
          <p style={{ fontSize: '1.2rem', marginBottom: '2.5rem', opacity: 0.9 }}>
            Join 150+ South African businesses already using Gerbo to capture more leads.
          </p>
          <button onClick={() => navigate('/register')} className="btn btn-primary" style={{ marginRight: '1rem' }}>
            Start Free Trial
          </button>
          <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="btn btn-outline-white">
            Send Us a Message
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" style={{ background: 'var(--text-dark)', color: 'var(--bg-white)', padding: '80px 2rem 40px' }}>
        <div className="footer-container" style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div className="footer-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '4rem', marginBottom: '4rem' }}>
            <div className="footer-column">
              <img src="/images/8EYtClIJaotk40RaLdi5bBD444-1.png" alt="Gerbo Logo" className="logo" style={{ marginBottom: '1.5rem' }} />
              <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1rem', lineHeight: 1.7 }}>
                Your friendly AI receptionist, always available to help your business grow.
              </p>
            </div>

            <div className="footer-column">
              <h4 style={{ fontSize: '1.1rem', fontWeight: 800, marginBottom: '1.5rem' }}>Product</h4>
              <ul className="footer-links" style={{ listStyle: 'none' }}>
                <li style={{ marginBottom: '1rem' }}><a href="/#features" style={{ color: 'rgba(255, 255, 255, 0.7)', textDecoration: 'none', fontSize: '1rem' }}>Features</a></li>
                <li style={{ marginBottom: '1rem' }}><a href="/#pricing" style={{ color: 'rgba(255, 255, 255, 0.7)', textDecoration: 'none', fontSize: '1rem' }}>Pricing</a></li>
                <li style={{ marginBottom: '1rem' }}><a href="/#industries" style={{ color: 'rgba(255, 255, 255, 0.7)', textDecoration: 'none', fontSize: '1rem' }}>Industries</a></li>
                <li style={{ marginBottom: '1rem' }}><a href="/blog" style={{ color: 'rgba(255, 255, 255, 0.7)', textDecoration: 'none', fontSize: '1rem' }}>Blog</a></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4 style={{ fontSize: '1.1rem', fontWeight: 800, marginBottom: '1.5rem' }}>Company</h4>
              <ul className="footer-links" style={{ listStyle: 'none' }}>
                <li style={{ marginBottom: '1rem' }}><a href="/contact" style={{ color: 'rgba(255, 255, 255, 0.7)', textDecoration: 'none', fontSize: '1rem' }}>Contact</a></li>
                <li style={{ marginBottom: '1rem' }}><a href="/privacy" style={{ color: 'rgba(255, 255, 255, 0.7)', textDecoration: 'none', fontSize: '1rem' }}>Privacy</a></li>
                <li style={{ marginBottom: '1rem' }}><a href="/terms" style={{ color: 'rgba(255, 255, 255, 0.7)', textDecoration: 'none', fontSize: '1rem' }}>Terms</a></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4 style={{ fontSize: '1.1rem', fontWeight: 800, marginBottom: '1.5rem' }}>Support</h4>
              <ul className="footer-links" style={{ listStyle: 'none' }}>
                <li style={{ marginBottom: '1rem' }}><a href="mailto:support@gerboai.com" style={{ color: 'rgba(255, 255, 255, 0.7)', textDecoration: 'none', fontSize: '1rem' }}>support@gerboai.com</a></li>
                <li style={{ marginBottom: '1rem' }}><a href="tel:+27684887039" style={{ color: 'rgba(255, 255, 255, 0.7)', textDecoration: 'none', fontSize: '1rem' }}>+27 68 488 7039</a></li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom" style={{ paddingTop: '2rem', borderTop: '1px solid rgba(255, 255, 255, 0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.95rem' }}>© 2025 Gerbo. All rights reserved.</p>
            <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.95rem' }}>Made by Solid5</p>
          </div>
        </div>
      </footer>
    </>
  );
};